import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MessageCircle, Phone, CheckCircle2, ChevronLeft } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { useLang } from "@/context/LanguageContext";
import { useData } from "@/context/DataContext";
import api from "@/lib/api";
import { PHONE_TEL, openWa } from "@/lib/wa";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { pick } = useLang();
  const [service, setService] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setService(null);
    setNotFound(false);
    api.get(`/services/${slug}`).then(({ data }) => setService(data)).catch(() => setNotFound(true));
    window.scrollTo(0, 0);
  }, [slug]);

  if (notFound) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-6 pt-[72px] text-center">
        <h1 className="text-2xl font-bold text-white">الخدمة غير موجودة</h1>
        <Link to="/services" className="btn-gold">جميع الخدمات</Link>
      </main>
    );
  }

  if (!service) return <div className="flex min-h-screen items-center justify-center pt-[72px]"><div className="h-10 w-10 animate-spin rounded-full border-2 border-gold border-t-transparent" /></div>;

  return (
    <main>
      <SEO title={`${pick(service.title_ar, service.title_en)} في الرياض | ميرو للإطارات وخدمات السيارات`} description={pick(service.short_ar, service.short_en)} path={`/services/${service.slug}`} image={service.image} type="article" />

      <section className="relative overflow-hidden pt-32 sm:pt-40">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 pb-16 lg:grid-cols-2 lg:px-8">
          <div>
            <p className="eyebrow mb-4">MERO SERVICE</p>
            <h1 className="text-3xl font-extrabold leading-snug text-white sm:text-4xl lg:text-5xl" data-testid="service-page-title">{pick(service.title_ar, service.title_en)}</h1>
            <p className="mt-5 text-base leading-9 text-mist sm:text-lg">{pick(service.short_ar, service.short_en)}</p>
            <div className="mt-8 flex flex-wrap gap-3.5">
              <button onClick={() => openWa(pick(service.wa_template_ar, service.wa_template_en))} className="btn-wa" data-testid="service-wa-cta"><MessageCircle size={18} /> {pick("استفسر عبر واتساب", "Ask on WhatsApp")}</button>
              <Link to="/order" className="btn-gold" data-testid="service-order-cta">{pick("اطلب هذه الخدمة", "Order this service")}</Link>
            </div>
          </div>
          <Reveal>
            <img src={service.image} alt={pick(service.title_ar, service.title_en)} className="w-full rounded-[2rem] border border-white/10 object-cover shadow-2xl shadow-black/50" />
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/5 bg-coal/40 py-16">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 lg:grid-cols-[1.3fr_1fr] lg:px-8">
          <Reveal>
            <h2 className="mb-6 text-2xl font-bold text-white">{pick("عن الخدمة", "About the service")}</h2>
            <p className="text-base leading-9 text-zinc-300">{pick(service.desc_ar, service.desc_en)}</p>
          </Reveal>
          <Reveal delay={1}>
            <div className="rounded-3xl border border-gold/20 bg-ink/70 p-8">
              <h3 className="mb-5 flex items-center gap-2 text-lg font-bold text-gold">{pick("متى تحتاج هذه الخدمة؟", "When do you need this?")}</h3>
              <ul className="space-y-4">
                {(pick(service.cases_ar, service.cases_en) || []).map((c, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm leading-7 text-zinc-300">
                    <CheckCircle2 size={18} className="mt-1 shrink-0 text-gold" /> {c}
                  </li>
                ))}
              </ul>
              <a href={PHONE_TEL} className="btn-ghost mt-7 w-full !py-3" data-testid="service-call-cta"><Phone size={16} /> 0507359999</a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-14">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <h2 className="mb-8 text-xl font-bold text-white">{pick("خدمات أخرى", "Other services")}</h2>
          <OtherServices current={service.slug} />
        </div>
      </section>
    </main>
  );
}

function OtherServices({ current }) {
  const { services } = useData();
  const { pick } = useLang();
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {services.filter((s) => s.slug !== current).slice(0, 4).map((s) => (
        <Link key={s.slug} to={s.slug === "tpms" ? "/tpms" : s.has_page ? `/services/${s.slug}` : "/services"} className="group rounded-2xl border border-white/10 bg-coal/60 p-6 transition-all hover:border-gold/40" data-testid={`other-service-${s.slug}`}>
          <h3 className="text-sm font-bold text-white group-hover:text-gold">{pick(s.title_ar, s.title_en)}</h3>
          <span className="mt-3 flex items-center gap-1.5 text-xs text-gold"><ChevronLeft size={13} /> تفاصيل</span>
        </Link>
      ))}
    </div>
  );
}
